import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Row,
    Select,
    Skeleton,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message
} from "antd";

import {
    CalendarOutlined,
    DollarOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    ReloadOutlined,
    RiseOutlined,
    WalletOutlined
} from "@ant-design/icons";

import {
    pdf
} from "@react-pdf/renderer";

import dayjs from "dayjs";

import api from "../api/api";

import {
    useAcademicYear
} from "../AcademicYearContext";

import IncomeStatementPDF from "./IncomeStatementPDF";


const {
    Title,
    Text
} = Typography;


const {
    RangePicker
} = DatePicker;


/* =============================================================
    CONFIGURATION
============================================================= */

const API_ENDPOINT =
    "/income-statement/";


const SCHOOL_NAME =
    "Zion Praise Academy - CRM School System";


const SCHOOL_LOCATION =
    "Cowfield 1st Junction Duportroad, Paynesville, Monrovia, Liberia";


/* =============================================================
    HELPERS
============================================================= */

const formatCurrency = (
    amount,
    currency = "LRD"
) => {
    const value = Number(
        amount || 0
    );

    if (
        currency === "USD"
    ) {
        return `$${value.toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        )}`;
    }

    return `LD ${value.toLocaleString(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }
    )}`;
};


const getNumber = (
    value
) => {
    return Number(
        value || 0
    );
};


/* =============================================================
    COMPONENT
============================================================= */

const IncomeStatement = () => {
    /* =========================================================
        GLOBAL ACADEMIC YEAR
    ========================================================= */

    const {
        selectedYear,
        loading: academicYearLoading,
        initialized: academicYearInitialized
    } = useAcademicYear();


    /* =========================================================
        LOCAL STATE
    ========================================================= */

    const [
        loading,
        setLoading
    ] = useState(
        false
    );

    const [
        exportingPDF,
        setExportingPDF
    ] = useState(
        false
    );

    const [
        incomeData,
        setIncomeData
    ] = useState(
        null
    );

    const [
        semester,
        setSemester
    ] = useState(
        null
    );

    const [
        dateRange,
        setDateRange
    ] = useState(
        null
    );


    /* =========================================================
        FETCH INCOME STATEMENT
    ========================================================= */

    const fetchIncomeStatement =
        useCallback(
            async () => {
                /*
                 * Never request the Income Statement
                 * without a selected Academic Year.
                 */
                if (
                    !selectedYear?.id
                ) {
                    setIncomeData(
                        null
                    );

                    return;
                }

                try {
                    setLoading(
                        true
                    );

                    const params = {
                        academic_year:
                            selectedYear.id
                    };

                    if (
                        semester
                    ) {
                        params.semester =
                            semester;
                    }

                    if (
                        dateRange
                        && dateRange.length === 2
                    ) {
                        params.start_date =
                            dateRange[0]
                                .format(
                                    "YYYY-MM-DD"
                                );

                        params.end_date =
                            dateRange[1]
                                .format(
                                    "YYYY-MM-DD"
                                );
                    }

                    const response =
                        await api.get(
                            API_ENDPOINT,
                            {
                                params
                            }
                        );

                    setIncomeData(
                        response.data
                    );

                } catch (
                    error
                ) {
                    console.error(
                        "Income Statement API error:",
                        error
                    );

                    message.error(
                        error?.response?.data?.detail
                        || "Unable to load the Income Statement."
                    );

                } finally {
                    setLoading(
                        false
                    );
                }
            },
            [
                selectedYear?.id,
                semester,
                dateRange
            ]
        );


    /* =========================================================
        AUTOMATIC ACADEMIC YEAR RELOAD
    ========================================================= */

    useEffect(
        () => {
            /*
             * Wait until AcademicYearContext
             * completes initialization.
             */
            if (
                !academicYearInitialized
            ) {
                return;
            }

            /*
             * The selectedYear is the single source
             * of truth for the entire application.
             *
             * Whenever it changes, this report
             * automatically reloads.
             */
            fetchIncomeStatement();

        },
        [
            academicYearInitialized,
            selectedYear?.id
        ]
    );


    /* =========================================================
        NORMALIZED REPORT DATA
    ========================================================= */

    const statement =
        useMemo(
            () => {
                return (
                    incomeData
                        ?.income_statement
                    || {}
                );
            },
            [
                incomeData
            ]
        );


    const tuition =
        statement.tuition
        || {};


    const scholarship =
        statement.scholarship
        || {};


    const ward =
        statement.ward
        || {};


    const totalIntake =
        statement.total_intake
        || {};


    const tuitionLRD =
        getNumber(
            tuition.lrd
        );


    const tuitionUSD =
        getNumber(
            tuition.usd
        );


    const scholarshipLRD =
        getNumber(
            scholarship.lrd
        );


    const scholarshipUSD =
        getNumber(
            scholarship.usd
        );


    const wardLRD =
        getNumber(
            ward.lrd
        );


    const wardUSD =
        getNumber(
            ward.usd
        );


    const totalLRD =
        getNumber(
            totalIntake.lrd
        );


    const totalUSD =
        getNumber(
            totalIntake.usd
        );


    /* =========================================================
        TABLE DATA
    ========================================================= */

    const tableData = [
        {
            key: "tuition",
            category: "Tuition",
            description:
                "Old Students + New Students",
            lrd: tuitionLRD,
            usd: tuitionUSD
        },

        {
            key: "scholarship",
            category: "Scholarship",
            description:
                "Scholarship Student Payments",
            lrd: scholarshipLRD,
            usd: scholarshipUSD
        },

        {
            key: "ward",
            category: "Ward",
            description:
                "Ward Student Payments",
            lrd: wardLRD,
            usd: wardUSD
        }
    ];


    /* =========================================================
        TABLE COLUMNS
    ========================================================= */

    const columns = [
        {
            title:
                "Income Category",

            dataIndex:
                "category",

            key:
                "category",

            render: (
                value,
                record
            ) => (
                <Space
                    direction="vertical"
                    size={0}
                >
                    <Text
                        strong
                        style={{
                            fontSize: 15,
                            color: "#111827"
                        }}
                    >
                        {value}
                    </Text>

                    <Text
                        type="secondary"
                        style={{
                            fontSize: 12
                        }}
                    >
                        {
                            record.description
                        }
                    </Text>
                </Space>
            )
        },

        {
            title:
                "Liberian Dollar (LRD)",

            dataIndex:
                "lrd",

            key:
                "lrd",

            align:
                "right",

            render: (
                value
            ) => (
                <Tag
                    style={{
                        fontSize: 14,
                        fontWeight: 800,
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: "none",
                        background: "#fff7ed",
                        color: "#ea580c"
                    }}
                >
                    {
                        formatCurrency(
                            value,
                            "LRD"
                        )
                    }
                </Tag>
            )
        },

        {
            title:
                "United States Dollar (USD)",

            dataIndex:
                "usd",

            key:
                "usd",

            align:
                "right",

            render: (
                value
            ) => (
                <Tag
                    style={{
                        fontSize: 14,
                        fontWeight: 800,
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: "none",
                        background: "#eff6ff",
                        color: "#1d4ed8"
                    }}
                >
                    {
                        formatCurrency(
                            value,
                            "USD"
                        )
                    }
                </Tag>
            )
        }
    ];


    /* =========================================================
        APPLY FILTERS
    ========================================================= */

    const handleApplyFilters =
        () => {
            fetchIncomeStatement();
        };


    /* =========================================================
        RESET FILTERS
    ========================================================= */

    const handleResetFilters =
        () => {
            setSemester(
                null
            );

            setDateRange(
                null
            );
        };


    /*
     * Reload after filter reset.
     */
    useEffect(
        () => {
            if (
                academicYearInitialized
                && selectedYear?.id
            ) {
                fetchIncomeStatement();
            }
        },
        [
            semester,
            dateRange
        ]
    );


    /* =========================================================
        EXPORT PDF
    ========================================================= */

    const handleExportPDF =
        async () => {
            try {
                if (
                    !incomeData
                ) {
                    message.warning(
                        "No Income Statement data is available."
                    );

                    return;
                }

                if (
                    !selectedYear?.id
                ) {
                    message.warning(
                        "Please select an Academic Year first."
                    );

                    return;
                }

                setExportingPDF(
                    true
                );

                const pdfDocument = (
                    <IncomeStatementPDF
                        incomeStatement={
                            incomeData
                        }
                        selectedYear={
                            selectedYear
                        }
                        semester={
                            semester
                        }
                        startDate={
                            dateRange?.[0]
                                ?.format(
                                    "YYYY-MM-DD"
                                )
                        }
                        endDate={
                            dateRange?.[1]
                                ?.format(
                                    "YYYY-MM-DD"
                                )
                        }
                        schoolName={
                            SCHOOL_NAME
                        }
                        schoolLocation={
                            SCHOOL_LOCATION
                        }
                    />
                );


                const blob =
                    await pdf(
                        pdfDocument
                    ).toBlob();


                const url =
                    URL.createObjectURL(
                        blob
                    );


                const link =
                    document.createElement(
                        "a"
                    );


                link.href =
                    url;


                link.download =
                    `Income_Statement_${
                        selectedYear.name
                            ?.replace(
                                /\s+/g,
                                "_"
                            )
                    }_${
                        dayjs().format(
                            "YYYY_MM_DD"
                        )
                    }.pdf`;


                document.body.appendChild(
                    link
                );


                link.click();


                document.body.removeChild(
                    link
                );


                URL.revokeObjectURL(
                    url
                );


                message.success(
                    "Income Statement PDF generated successfully."
                );

            } catch (
                error
            ) {
                console.error(
                    "PDF generation error:",
                    error
                );

                message.error(
                    "Unable to generate the Income Statement PDF."
                );

            } finally {
                setExportingPDF(
                    false
                );
            }
        };


    /* =========================================================
        RENDER
    ========================================================= */

    return (
        <div
            style={{
                minHeight:
                    "100vh",

                padding:
                    20,

                background:
                    "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)"
            }}
        >
            <style>
                {`

                    .income-statement-table
                    .ant-table-thead > tr > th {
                        background: #111827 !important;
                        color: #ffffff !important;
                        font-weight: 800 !important;
                        border-bottom: none !important;
                    }

                    .income-statement-table
                    .ant-table-tbody > tr:hover > td {
                        background: #fff7ed !important;
                    }

                    .income-statement-table
                    .ant-table-tbody > tr > td {
                        padding-top: 18px !important;
                        padding-bottom: 18px !important;
                    }

                    .income-statement-card {
                        transition:
                            transform 0.25s ease,
                            box-shadow 0.25s ease;
                    }

                    .income-statement-card:hover {
                        transform:
                            translateY(-4px);

                        box-shadow:
                            0 18px 45px
                            rgba(
                                17,
                                24,
                                39,
                                0.10
                            )
                            !important;
                    }

                    @media (
                        max-width: 768px
                    ) {

                        .income-page-header {
                            flex-direction:
                                column !important;

                            align-items:
                                flex-start !important;
                        }

                        .income-action-buttons {
                            width:
                                100% !important;
                        }

                        .income-action-buttons
                        .ant-btn {
                            flex:
                                1 !important;
                        }

                    }
                `}
            </style>


            {/* PAGE HEADER */}

            <div
                className="income-page-header"
                style={{
                    display:
                        "flex",

                    justifyContent:
                        "space-between",

                    alignItems:
                        "center",

                    gap:
                        20,

                    marginBottom:
                        24
                }}
            >
                <Space
                    align="center"
                    size={12}
                >
                    <div
                        style={{
                            width: 54,
                            height: 54,
                            borderRadius: 15,
                            display: "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                            background:
                                "linear-gradient(135deg, #f97316, #ea580c)",
                            color:
                                "#ffffff",
                            boxShadow:
                                "0 10px 25px rgba(249,115,22,0.25)"
                        }}
                    >
                        <RiseOutlined
                            style={{
                                fontSize: 25
                            }}
                        />
                    </div>


                    <div>
                        <Title
                            level={2}
                            style={{
                                margin: 0,
                                color:
                                    "#111827",
                                fontWeight:
                                    900
                            }}
                        >
                            Income Statement
                        </Title>


                        <Space
                            size={8}
                        >
                            <Text type="secondary">
                                Financial intake summary
                            </Text>

                            {
                                selectedYear ? (
                                    <Tag
                                        icon={
                                            <CalendarOutlined />
                                        }
                                        style={{
                                            borderRadius: 20,
                                            padding:
                                                "3px 10px",
                                            fontWeight:
                                                700,
                                            border:
                                                "1px solid #fed7aa",
                                            background:
                                                "#fff7ed",
                                            color:
                                                "#c2410c"
                                        }}
                                    >
                                        Academic Year:
                                        {" "}
                                        {
                                            selectedYear.name
                                        }
                                    </Tag>
                                ) : null
                            }
                        </Space>
                    </div>
                </Space>


                <Space
                    className="income-action-buttons"
                    wrap
                >
                    <Tooltip
                        title="Refresh Income Statement"
                    >
                        <Button
                            icon={
                                <ReloadOutlined />
                            }
                            loading={
                                loading
                            }
                            disabled={
                                !selectedYear?.id
                            }
                            onClick={
                                handleApplyFilters
                            }
                        >
                            Refresh
                        </Button>
                    </Tooltip>


                    <Button
                        type="primary"
                        icon={
                            <FilePdfOutlined />
                        }
                        loading={
                            exportingPDF
                        }
                        disabled={
                            !incomeData
                            || !selectedYear?.id
                        }
                        onClick={
                            handleExportPDF
                        }
                        style={{
                            background:
                                "linear-gradient(135deg, #f97316, #ea580c)",
                            border:
                                "none",
                            fontWeight:
                                800
                        }}
                    >
                        Download PDF
                    </Button>
                </Space>
            </div>


            {/* NO ACADEMIC YEAR */}

            {
                academicYearInitialized
                && !selectedYear
                ? (
                    <Alert
                        type="warning"
                        showIcon
                        message="No Academic Year Selected"
                        description={
                            "Please select an Academic Year before viewing the Income Statement."
                        }
                        style={{
                            marginBottom:
                                24,
                            borderRadius:
                                12
                        }}
                    />
                )
                : null
            }


            {/* FILTERS */}

            <Card
                bordered={false}
                style={{
                    borderRadius:
                        16,

                    marginBottom:
                        24,

                    boxShadow:
                        "0 8px 30px rgba(17,24,39,0.06)"
                }}
            >
                <Row
                    gutter={[
                        16,
                        16
                    ]}
                    align="bottom"
                >
                    {/* SELECTED ACADEMIC YEAR */}

                    <Col
                        xs={24}
                        md={6}
                    >
                        <Text
                            strong
                        >
                            Academic Year
                        </Text>

                        <div
                            style={{
                                marginTop:
                                    7,

                                padding:
                                    "8px 12px",

                                borderRadius:
                                    8,

                                border:
                                    "1px solid #e5e7eb",

                                background:
                                    "#f9fafb",

                                fontWeight:
                                    700,

                                color:
                                    "#111827"
                            }}
                        >
                            {
                                selectedYear?.name
                                || "No Academic Year Selected"
                            }
                        </div>
                    </Col>


                    {/* SEMESTER */}

                    <Col
                        xs={24}
                        sm={12}
                        md={6}
                    >
                        <Text
                            strong
                        >
                            Semester
                        </Text>

                        <Select
                            placeholder={
                                "All Semesters"
                            }
                            value={
                                semester
                            }
                            onChange={
                                setSemester
                            }
                            allowClear
                            style={{
                                width:
                                    "100%",
                                marginTop:
                                    7
                            }}
                            options={[
                                {
                                    value:
                                        "First",
                                    label:
                                        "First Semester"
                                },

                                {
                                    value:
                                        "Second",
                                    label:
                                        "Second Semester"
                                }
                            ]}
                        />
                    </Col>


                    {/* DATE RANGE */}

                    <Col
                        xs={24}
                        sm={12}
                        md={8}
                    >
                        <Text
                            strong
                        >
                            Reporting Period
                        </Text>

                        <RangePicker
                            value={
                                dateRange
                            }
                            onChange={
                                setDateRange
                            }
                            style={{
                                width:
                                    "100%",
                                marginTop:
                                    7
                            }}
                        />
                    </Col>


                    {/* ACTIONS */}

                    <Col
                        xs={24}
                        md={4}
                    >
                        <Space
                            style={{
                                width:
                                    "100%",
                                marginTop:
                                    7
                            }}
                        >
                            <Button
                                type="primary"
                                onClick={
                                    handleApplyFilters
                                }
                                loading={
                                    loading
                                }
                                disabled={
                                    !selectedYear?.id
                                }
                                style={{
                                    flex:
                                        1,

                                    background:
                                        "#111827",

                                    border:
                                        "none",

                                    fontWeight:
                                        700
                                }}
                            >
                                Apply
                            </Button>


                            <Button
                                onClick={
                                    handleResetFilters
                                }
                            >
                                Reset
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>


            {/* LOADING */}

            {
                (
                    academicYearLoading
                    || (
                        loading
                        && !incomeData
                    )
                )
                ? (
                    <Card
                        bordered={false}
                        style={{
                            borderRadius:
                                16
                        }}
                    >
                        <Skeleton
                            active
                            paragraph={{
                                rows:
                                    12
                            }}
                        />
                    </Card>
                )
                : null
            }


            {/* REPORT CONTENT */}

            {
                academicYearInitialized
                && selectedYear
                && !(
                    academicYearLoading
                    || (
                        loading
                        && !incomeData
                    )
                )
                ? (
                    <>
                        {/* SUMMARY CARDS */}

                        <Row
                            gutter={[
                                18,
                                18
                            ]}
                            style={{
                                marginBottom:
                                    24
                            }}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    className="income-statement-card"
                                    bordered={false}
                                    style={{
                                        borderRadius:
                                            16,

                                        background:
                                            "linear-gradient(135deg, #111827, #1f2937)",

                                        boxShadow:
                                            "0 10px 30px rgba(17,24,39,0.12)"
                                    }}
                                >
                                    <Space
                                        align="start"
                                        size={14}
                                    >
                                        <WalletOutlined
                                            style={{
                                                fontSize:
                                                    28,
                                                color:
                                                    "#f97316"
                                            }}
                                        />

                                        <div>
                                            <Text
                                                style={{
                                                    color:
                                                        "#d1d5db"
                                                }}
                                            >
                                                Total Intake
                                                — LRD
                                            </Text>

                                            <div
                                                style={{
                                                    marginTop:
                                                        8
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        color:
                                                            "#ffffff",
                                                        fontSize:
                                                            22,
                                                        fontWeight:
                                                            900
                                                    }}
                                                >
                                                    {
                                                        formatCurrency(
                                                            totalLRD,
                                                            "LRD"
                                                        )
                                                    }
                                                </Text>
                                            </div>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>


                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    className="income-statement-card"
                                    bordered={false}
                                    style={{
                                        borderRadius:
                                            16,

                                        background:
                                            "linear-gradient(135deg, #1d4ed8, #2563eb)",

                                        boxShadow:
                                            "0 10px 30px rgba(37,99,235,0.15)"
                                    }}
                                >
                                    <Space
                                        align="start"
                                        size={14}
                                    >
                                        <DollarOutlined
                                            style={{
                                                fontSize:
                                                    28,
                                                color:
                                                    "#ffffff"
                                            }}
                                        />

                                        <div>
                                            <Text
                                                style={{
                                                    color:
                                                        "#dbeafe"
                                                }}
                                            >
                                                Total Intake
                                                — USD
                                            </Text>

                                            <div
                                                style={{
                                                    marginTop:
                                                        8
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        color:
                                                            "#ffffff",
                                                        fontSize:
                                                            22,
                                                        fontWeight:
                                                            900
                                                    }}
                                                >
                                                    {
                                                        formatCurrency(
                                                            totalUSD,
                                                            "USD"
                                                        )
                                                    }
                                                </Text>
                                            </div>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>


                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    className="income-statement-card"
                                    bordered={false}
                                    style={{
                                        borderRadius:
                                            16,

                                        boxShadow:
                                            "0 8px 30px rgba(17,24,39,0.06)"
                                    }}
                                >
                                    <Statistic
                                        title={
                                            "Tuition Income — LRD"
                                        }
                                        value={
                                            tuitionLRD
                                        }
                                        prefix={
                                            "LD "
                                        }
                                        precision={
                                            2
                                        }
                                        groupSeparator={
                                            ","
                                        }
                                        valueStyle={{
                                            fontWeight:
                                                900,
                                            color:
                                                "#f97316"
                                        }}
                                    />

                                    <Text
                                        type="secondary"
                                    >
                                        Old + New Students
                                    </Text>
                                </Card>
                            </Col>


                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Card
                                    className="income-statement-card"
                                    bordered={false}
                                    style={{
                                        borderRadius:
                                            16,

                                        boxShadow:
                                            "0 8px 30px rgba(17,24,39,0.06)"
                                    }}
                                >
                                    <Statistic
                                        title={
                                            "Special Intake — LRD"
                                        }
                                        value={
                                            scholarshipLRD
                                            + wardLRD
                                        }
                                        prefix={
                                            "LD "
                                        }
                                        precision={
                                            2
                                        }
                                        groupSeparator={
                                            ","
                                        }
                                        valueStyle={{
                                            fontWeight:
                                                900,
                                            color:
                                                "#111827"
                                        }}
                                    />

                                    <Text
                                        type="secondary"
                                    >
                                        Scholarship + Ward
                                    </Text>
                                </Card>
                            </Col>
                        </Row>


                        {/* TABLE */}

                        <Card
                            bordered={false}
                            style={{
                                borderRadius:
                                    16,

                                marginBottom:
                                    24,

                                boxShadow:
                                    "0 8px 30px rgba(17,24,39,0.06)"
                            }}
                            title={
                                <Space>
                                    <RiseOutlined
                                        style={{
                                            color:
                                                "#f97316"
                                        }}
                                    />

                                    <Text
                                        strong
                                        style={{
                                            fontSize:
                                                17
                                        }}
                                    >
                                        Revenue Breakdown
                                    </Text>
                                </Space>
                            }
                        >
                            <Table
                                className="income-statement-table"
                                columns={
                                    columns
                                }
                                dataSource={
                                    tableData
                                }
                                loading={
                                    loading
                                }
                                pagination={
                                    false
                                }
                                scroll={{
                                    x:
                                        700
                                }}
                                locale={{
                                    emptyText: (
                                        <Empty
                                            description={
                                                "No income data available for the selected Academic Year."
                                            }
                                        />
                                    )
                                }}
                                summary={() => (
                                    <Table.Summary>
                                        <Table.Summary.Row
                                            style={{
                                                background:
                                                    "#111827"
                                            }}
                                        >
                                            <Table.Summary.Cell
                                                index={0}
                                            >
                                                <Text
                                                    style={{
                                                        color:
                                                            "#ffffff",
                                                        fontWeight:
                                                            900,
                                                        fontSize:
                                                            15
                                                    }}
                                                >
                                                    TOTAL
                                                    INTAKE
                                                </Text>
                                            </Table.Summary.Cell>


                                            <Table.Summary.Cell
                                                index={1}
                                                align="right"
                                            >
                                                <Text
                                                    style={{
                                                        color:
                                                            "#f97316",
                                                        fontWeight:
                                                            900,
                                                        fontSize:
                                                            15
                                                    }}
                                                >
                                                    {
                                                        formatCurrency(
                                                            totalLRD,
                                                            "LRD"
                                                        )
                                                    }
                                                </Text>
                                            </Table.Summary.Cell>


                                            <Table.Summary.Cell
                                                index={2}
                                                align="right"
                                            >
                                                <Text
                                                    style={{
                                                        color:
                                                            "#ffffff",
                                                        fontWeight:
                                                            900,
                                                        fontSize:
                                                            15
                                                    }}
                                                >
                                                    {
                                                        formatCurrency(
                                                            totalUSD,
                                                            "USD"
                                                        )
                                                    }
                                                </Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                            />
                        </Card>


                        {/* REPORT INFORMATION */}

                        <Card
                            bordered={false}
                            style={{
                                borderRadius:
                                    16,

                                background:
                                    "linear-gradient(135deg, #fff7ed, #ffffff)",

                                border:
                                    "1px solid #fed7aa"
                            }}
                        >
                            <Row
                                gutter={[
                                    20,
                                    20
                                ]}
                                align="middle"
                            >
                                <Col
                                    xs={24}
                                    md={16}
                                >
                                    <Space
                                        direction="vertical"
                                        size={4}
                                    >
                                        <Text
                                            strong
                                            style={{
                                                fontSize:
                                                    16,

                                                color:
                                                    "#111827"
                                            }}
                                        >
                                            {
                                                selectedYear?.name
                                            }
                                            {" "}
                                            Income Statement
                                        </Text>

                                        <Text
                                            type="secondary"
                                        >
                                            This report displays
                                            actual Tuition,
                                            Scholarship, and Ward
                                            payments received for
                                            the currently selected
                                            Academic Year. LRD and
                                            USD are reported
                                            separately.
                                        </Text>
                                    </Space>
                                </Col>


                                <Col
                                    xs={24}
                                    md={8}
                                    style={{
                                        textAlign:
                                            "right"
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={
                                            <DownloadOutlined />
                                        }
                                        loading={
                                            exportingPDF
                                        }
                                        onClick={
                                            handleExportPDF
                                        }
                                        style={{
                                            background:
                                                "#f97316",

                                            border:
                                                "none",

                                            fontWeight:
                                                800
                                        }}
                                    >
                                        Export Official
                                        Statement
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    </>
                )
                : null
            }
        </div>
    );
};
export default IncomeStatement;
